import { FC } from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import { TodaysPriceCellProps } from './TodaysPriceCell.props';

// Only rendered for an override, or where one is allowed; otherwise the column stays a plain number.
const TodaysPriceCell: FC<TodaysPriceCellProps> = ({ row, currencyCode, canOverride, onOverrideClick }) => {
  const todaysPrice = row.record.todays_price;
  const isOverride = Boolean(todaysPrice?.is_daily_nav_override);
  // Company rows only, so this is the column's own value: the common share price.
  const price = todaysPrice?.common_share_value;
  const label =
    price == null
      ? ''
      : Number(price).toLocaleString('en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

  return (
    // Pencil pinned left, shown on hover (or keyboard focus, so it stays reachable);
    // chip and price right-aligned (forceComponent bypasses the td's text-align).
    <Box
      display="flex"
      alignItems="center"
      gap={0.5}
      height="100%"
      sx={{
        '& .override-price-button': { opacity: 0, transition: 'opacity 120ms' },
        '&:hover .override-price-button, & .override-price-button:focus-visible': { opacity: 1 },
      }}>
      {canOverride && (
        <Tooltip title="Override today's price">
          <IconButton
            id={`override-price-button-${row.companyId}`}
            className="override-price-button"
            aria-label={`Override today's price for ${row.companyName}`}
            size="small"
            onClick={() => onOverrideClick(row)}
            sx={{ padding: '2px' }}>
            <EditOutlinedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
      {/* 20px: the grid's .text-cell padding, so the price lines up with plain number cells. */}
      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5} flex={1} pr="20px">
        {isOverride && (
          <Chip
            label="Override"
            size="small"
            data-testid={`todays-price-override-chip-${row.companyId}`}
            sx={{ fontSize: 9, height: 16 }}
          />
        )}
        <span>{label}</span>
      </Box>
    </Box>
  );
};

export default TodaysPriceCell;
